import {
	forwardRef,
	Fragment,
	PropsWithChildren,
	ReactElement,
	ReactNode,
	Ref,
	RefAttributes,
	RefObject,
	useCallback,
	useImperativeHandle,
	useMemo
} from "react";
import { DropOver } from "../DropOver/DropOver";
import { ActionListBox } from "../ActionListBox/ActionListBox";
import {
	KeyBoardListNavigationContext,
	KeyBoardListNavigationContextData,
	useKeyboardListNavigationItem
} from "client/Hooks";
import { never } from "client_legacy/ndaw/errors";
import { BusyIndicator } from "../BusyIndicator/BusyIndicator";
import { NoResults, NoResultsProps } from "../NoResults/NoResults";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./SuggestionBox.module.less";
import { ActionList } from "../ActionList/ActionList";
import {
	convertArbitraryValueForTrackingId,
	generateTrackingId,
	isNotNil,
	isNotNilOrEmpty,
	setTrackingId
} from "client/Misc/utils";
import { ActionListItem } from "../ActionList/ActionListItem";
import { ActionListDivider } from "../ActionList/ActionListDivider";
import { ActionListHeader } from "../ActionList/ActionListHeader";
import {
	MultiComboBoxItem,
	SuggestedMultiComboBoxItem
} from "./MultiComboBox";
import { ActionListNoResults } from "../ActionList/ActionListNoResults";
import { Key } from "client/Misc/commonTypes";

export type EmptySearchTermBehavior = "search" | "show-hint";

export type MultiComboBoxSuggestionsResult<TValue, TRenderData = undefined> = {
	loadingStatus: "loading" | "error";
} | {
	loadingStatus: "ready";
	suggestions: readonly SuggestedMultiComboBoxItem<TValue, TRenderData>[];
};

export type BaseSuggestionBoxProps<TValue> = {
	target: HTMLElement;
	targetWidth: number;
	searchTerm: string;
	keyboardNavigationContext: KeyBoardListNavigationContextData;
	trackingId: string;
	onSelectItem: (suggestion: MultiComboBoxItem<TValue>) => void;
	onClose: () => void;
	onRetrySearch: () => void;
};

export type MultiComboBoxSuggestionBoxHandle<TValue> = {
	getTopSuggestion: () => MultiComboBoxItem<TValue> | undefined;
};

export type MultiComboBoxSuggestionBoxProps<TValue, TRenderData = undefined> = {
	baseProps: BaseSuggestionBoxProps<TValue>;
	suggestionsResult: MultiComboBoxSuggestionsResult<TValue, TRenderData>;
	customSuggestion?: (searchTerm: string) => SuggestedMultiComboBoxItem<TValue, TRenderData> | null;
	emptySearchTermBehavior?: EmptySearchTermBehavior;
	menuTitle: string;
	children?: (
		suggestion: SuggestedMultiComboBoxItem<TValue, TRenderData>,
		execute: () => void
	) => ReactElement;
};

const SuggestionBoxInternal = forwardRef(
	<TValue, TRenderData>(
		props: MultiComboBoxSuggestionBoxProps<TValue, TRenderData>,
		ref: Ref<MultiComboBoxSuggestionBoxHandle<TValue>>
	): ReactElement => {
		const {
			baseProps: {
				target,
				targetWidth,
				keyboardNavigationContext,
				searchTerm,
				trackingId,
				onClose,
				onSelectItem,
				onRetrySearch
			},
			suggestionsResult,
			customSuggestion,
			emptySearchTermBehavior,
			menuTitle,
			children
		} = props;

		const customItem = useMemo(
			(): SuggestedMultiComboBoxItem<TValue, TRenderData> | nil => customSuggestion?.(searchTerm),
			[customSuggestion, searchTerm]
		);

		const suggestionsToRender = useMemo(
			(): readonly SuggestedMultiComboBoxItem<TValue, TRenderData>[] => {
				switch(suggestionsResult.loadingStatus) {
					case "loading":
						return [];
					case "error":
						return customItem
							? [customItem]
							: [];
					case "ready":
						return customItem
							? [
								customItem,
								...suggestionsResult.suggestions
							]
							: suggestionsResult.suggestions;
					default:
						never(suggestionsResult);
				}
			},
			[
				suggestionsResult,
				customItem
			]
		);

		useImperativeHandle(
			ref,
			(): MultiComboBoxSuggestionBoxHandle<TValue> => ({
				getTopSuggestion: (): SuggestedMultiComboBoxItem<TValue, TRenderData> | undefined => {
					return suggestionsToRender.length > 0
						? suggestionsToRender[0]
						: undefined;
				}
			}),
			[suggestionsToRender]
		);

		const renderContent = (): ReactElement => {
			if (suggestionsResult.loadingStatus === "loading") {
				return <LoadingScreen />;
			}

			if (suggestionsResult.loadingStatus === "ready" && suggestionsToRender.length === 0) {
				return (
					<NoResultsScreen
						searchTerm={searchTerm}
						emptySearchTermBehavior={emptySearchTermBehavior}
					/>
				);
			}

			return (
				<KeyBoardListNavigationContext.Provider value={keyboardNavigationContext}>
					<List<TValue, TRenderData>
						suggestionsToRender={suggestionsToRender}
						onSelectItem={onSelectItem}
						hasError={suggestionsResult.loadingStatus === "error"}
						onRetrySearch={onRetrySearch}
						renderItem={children}
					/>
				</KeyBoardListNavigationContext.Provider>
			);
		};

		return (
			<Container
				target={target}
				targetWidth={targetWidth}
				menuTitle={menuTitle}
				onClose={onClose}
				trackingId={trackingId}
			>
				{renderContent()}
			</Container>
		);
	}
);
SuggestionBoxInternal.displayName = "MultiComboBox.SuggestionBox";

export const SuggestionBox = SuggestionBoxInternal as unknown as {
	<TValue>(props: MultiComboBoxSuggestionBoxProps<TValue> & RefAttributes<MultiComboBoxSuggestionBoxHandle<TValue>>): ReactElement;
	displayName: string;
	Container: typeof Container;
	LoadingScreen: typeof LoadingScreen;
	NoResultsScreen: typeof NoResultsScreen;
	List: typeof List;
};

export type ContainerProps = {
	target: HTMLElement;
	targetWidth: number;
	menuTitle: string;
	onClose: () => void;
	trackingId: string;
};

export const Container = (props: PropsWithChildren<ContainerProps>): ReactElement => {
	const {
		target,
		targetWidth,
		menuTitle,
		onClose,
		children,
		trackingId
	} = props;

	const outsideClickIgnoreRefs = useMemo(
		(): readonly RefObject<HTMLElement>[] => {
			const targetRef: RefObject<HTMLElement> = {
				current: target
			};

			return [targetRef];
		},
		[target]
	);

	return (
		<DropOver
			outsideClickIgnoreRefs={outsideClickIgnoreRefs}
			target={target}
			onClose={onClose}
			hasResponsiveDropOver={false}
		>
			<ActionListBox
				isHeightConstrainted
				responsiveMode={{ kind: "never"}}
				width={targetWidth}
				menuTitle={menuTitle}
				trackingId={trackingId}
			>
				{children}
			</ActionListBox>
		</DropOver>
	);
};
Container.displayName = "MultiComboBox.SuggestionBox.Container";

const LoadingScreen = (): ReactElement => {

	return (
		<div className={styles.busyIndicatorHost}>
			<BusyIndicator />
		</div>
	);
};
LoadingScreen.displayName = "MultiComboBox.SuggestionBox.LoadingScreen";

type NoResultsScreenProps = {
	searchTerm: string;
	emptySearchTermBehavior?: EmptySearchTermBehavior;
};

const NoResultsScreen = (props: NoResultsScreenProps): ReactElement => {
	const {
		searchTerm,
		emptySearchTermBehavior = "search"
	} = props;

	const shouldShowEmptySearchHint: boolean = searchTerm === ""
		&& emptySearchTermBehavior === "show-hint";

	return (
		<NoResults
			icon="magnifying-glass"
			title={
				localize(
					shouldShowEmptySearchHint
						? "_EnterASearchTerm"
						: "_NoSearchResults"
				)
			}
			message={
				localize(
					shouldShowEmptySearchHint
						? "_PleaseEnterASearchTermToBeginYourSearch"
						: "_PleaseTrySearchingWithAnotherTerm"
				)
			}
			trackingId="no-results"
		/>
	);
};
NoResultsScreen.displayName = "MultiComboBox.SuggestionBox.NoResultsScreen";

type SuggestionsListRendererProps<TValue, TRenderData> = {
	suggestionsToRender: readonly SuggestedMultiComboBoxItem<TValue, TRenderData>[];
	hasError: boolean;
	onRetrySearch: () => void;
	list: (children: ReactNode) => ReactElement;
	listItem: (
		index: number,
		suggestion: SuggestedMultiComboBoxItem<TValue, TRenderData>
	) => ReactElement;
	groupHeader: (name: string) => ReactElement;
	divider: () => ReactElement;
	noResult: (props: NoResultsProps) => ReactElement;
};

const SuggestionsListRenderer = <TValue, TRenderData>(props: SuggestionsListRendererProps<TValue, TRenderData>): ReactElement => {
	const {
		suggestionsToRender,
		hasError,
		onRetrySearch,
		list,
		listItem,
		groupHeader,
		divider,
		noResult
	} = props;

	const hasSuggestions: boolean = suggestionsToRender.length > 0;
	let lastGroupName: string | undefined = undefined;

	return list(
		<>
			{hasSuggestions && suggestionsToRender.map(
				(item, index): ReactElement => {
					let hasDivider: boolean = false;

					if (item.group?.name !== lastGroupName) {
						if (index > 0) {
							hasDivider = true;
						}

						lastGroupName = item.group?.name;
					}

					const title: string | undefined = item.group
						?.title;

					return (
						<Fragment key={getItemKey(item, index)}>
							{hasDivider && divider()}
							{(hasDivider || index === 0)
								&& isNotNil(title)
								&& groupHeader(title)
							}

							{listItem(
								index,
								item
							)}
						</Fragment>
					);
				})
			}

			{hasError && (
				<>
					{hasSuggestions && divider()}

					{noResult({
						icon: "gear",
						title: localize("_UnableToRetrieveItems"),
						message: localize("_WeWereUnableToRetrieveTheItems"),
						trackingId: "error-message",
						onRetry: onRetrySearch
					})}
				</>
			)}
		</>
	);
};
SuggestionsListRenderer.displayName = "MultiComboBox.SuggestionBox.SuggestionsListRenderer";

type ListProps<TValue, TRenderData> = {
	suggestionsToRender: readonly SuggestedMultiComboBoxItem<TValue, TRenderData>[];
	onSelectItem: (item: MultiComboBoxItem<TValue>) => void;
	hasError: boolean;
	onRetrySearch: () => void;
	renderItem?: (
		suggestion: SuggestedMultiComboBoxItem<TValue, TRenderData>,
		execute: () => void
	) => ReactElement;
};

const List = <TValue, TRenderData>(props: ListProps<TValue, TRenderData>): ReactElement => {
	const {
		renderItem,
		onSelectItem,
		...otherProps
	} = props;

	const suggestionTrackingId: string = "suggestions";

	if (renderItem) {
		return (
			<SuggestionsListRenderer<TValue, TRenderData>
				{...otherProps}
				list={(children) => (
					<ul
						{...setTrackingId(suggestionTrackingId)}
						className={styles.customList}
					>
						{children}
					</ul>
				)}
				listItem={(
					index,
					item
				): ReactElement => (
					<CustomListItem
						index={index}
						item={item}
						onSelectItem={onSelectItem}
						renderItem={renderItem}
					/>
				)}
				groupHeader={(name): ReactElement => (
					<CustomGroupHeader name={name} />
				)}
				divider={(): ReactElement => (
					<li className={styles.customDivider} />
				)}
				noResult={(props): ReactElement => (
					<li>
						<NoResults {...props} />
					</li>
				)}
			/>
		);
	}

	return (
		<SuggestionsListRenderer
			{...otherProps}
			list={(children) => (
				<ActionList
					width="fill"
					trackingId={suggestionTrackingId}
				>
					{children}
				</ActionList>
			)}
			listItem={(
				index,
				item
			): ReactElement => (
				<ListItem
					index={index}
					item={item}
					onSelectItem={onSelectItem}
				/>
			)}
			groupHeader={(name): ReactElement => (
				<ActionListHeader text={name} />
			)}
			divider={(): ReactElement => <ActionListDivider />}
			noResult={(props): ReactElement => (
				<ActionListNoResults
					{...props}
				/>
			)}
		/>
	);
};
List.displayName = "MultiComboBox.SuggestionBox.List";

SuggestionBox.Container = Container;
SuggestionBox.LoadingScreen = LoadingScreen;
SuggestionBox.NoResultsScreen = NoResultsScreen;
SuggestionBox.List = List;

type ListItemProps<TValue, TRenderData> = {
	index: number;
	item: SuggestedMultiComboBoxItem<TValue, TRenderData>;
	onSelectItem: (value: SuggestedMultiComboBoxItem<TValue, TRenderData>) => void;
};

const ListItem = <TValue, TRenderData>(props: ListItemProps<TValue, TRenderData>): ReactElement => {
	const {
		index,
		item,
		onSelectItem
	} = props;

	const {
		onKeyDown,
		ref
	} = useKeyboardListNavigationItem({
		itemIndex: index,
		onExecute: () => onSelectItem(item),
		executeKey: [
			Key.Enter,
			Key.Space
		],
		shouldCancelOnTab: true
	});

	return (
		<ActionListItem
			ref={ref}
			onKeyDown={onKeyDown}
			text={
				isNotNil(item.optionPrefix)
					? `${item.optionPrefix}${item.text}`
					: item.text
			}
			highlight={item.highlight}
			onClick={() => onSelectItem(item)}
			trackingId={generateTrackingId("value", `|${getItemValueTrackingId(item)}`)}
			shouldNotAutoCloseContainingDropOver
			tabIndex={-1}
		/>
	);
};
ListItem.displayName = "MultiComboBox.SuggestionBox.ListItem";

type CustomListItemProps<TValue, TRenderData> = {
	index: number;
	item: SuggestedMultiComboBoxItem<TValue, TRenderData>;
	onSelectItem: (value: SuggestedMultiComboBoxItem<TValue, TRenderData>) => void;
	renderItem: (
		suggestion: SuggestedMultiComboBoxItem<TValue, TRenderData>,
		execute: () => void
	) => ReactElement;
};

const CustomListItem = <TValue, TRenderData>(props: CustomListItemProps<TValue, TRenderData>): ReactElement => {
	const {
		index,
		item,
		onSelectItem,
		renderItem
	} = props;

	const executeHandler = useCallback(
		(): void => {
			onSelectItem(item);
		},
		[item, onSelectItem]
	);

	const {
		onKeyDown,
		ref
	} = useKeyboardListNavigationItem({
		itemIndex: index,
		executeKey: [
			Key.Enter,
			Key.Space
		],
		onExecute: executeHandler,
		shouldCancelOnTab: true
	});

	return (
		<li
			className={styles.customListItem}
			ref={ref}
			onKeyDown={onKeyDown}
			tabIndex={-1}
		>
			{renderItem(item, executeHandler)}
		</li>
	);
};
CustomListItem.displayName = "MultiComboBox.SuggestionBox.CustomListItem";

type CustomGroupHeaderProps = {
	name: string;
};

const CustomGroupHeader = (props: CustomGroupHeaderProps): ReactElement => {
	const {
		name
	} = props;

	return (
		<li className={styles.customGroupHeader}>
			<span className={styles.customGroupHeaderText}>
				{name}
			</span>
		</li>
	);
};
CustomGroupHeader.displayName = "MultiComboBox.SuggestionBox.CustomGroupHeader";

export function getItemValueTrackingId<TValue>(item: MultiComboBoxItem<TValue>): string {
	if (isNotNilOrEmpty(item.valueTrackingId)) {
		return item.valueTrackingId;
	}

	return convertArbitraryValueForTrackingId(item.value);
}

function getItemKey<TValue>(item: MultiComboBoxItem<TValue>, index: number): string {
	if (isNotNilOrEmpty(item.valueTrackingId)) {
		return `tid-${item.valueTrackingId}`;
	}

	if (["number", "string"].includes(typeof item.value)) {
		return `value-${item.value}`;
	}

	return `index-${index}`;
};